import { useFilters } from '@domain-group/react-hooks'
import { Box, DashboardModule } from '@domain-group/suit-theme'

import DashboardModuleFilters from '@components/DashboardModuleFilters'
import useTemplatePages from '@hooks/useTemplatePages'

import TemplatePageListItem from './TemplatePageListItem'

const buildPageFilterFields = (allowedFilters: string[]) => {
  const boxProps = { marginBottom: 'large' }
  const style = { marginBottom: 16 }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Template Title or ID',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        endpoint: 'service_products',
        entityKey: 'serviceProducts',
      },
      hidden: !allowedFilters.includes('service_product_id'),
      key: 'service_product_id',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'agencies',
      },
      hidden: !allowedFilters.includes('agency_id'),
      key: 'agency_id',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'brands',
      },
      hidden: !allowedFilters.includes('brand_id'),
      key: 'brand_id',
    },
  ]
}

interface TemplatePageListProps {
  allowedFilters: string[],
  filterIds?: {
    agency_id?: number,
    provider_id?: number,
    publication_id?: number,
    serviceProduct_id?: number,
  },
}

const TemplatePageList = (props: TemplatePageListProps) => {
  const {
    allowedFilters, filterIds,
  } = props

  const filterPayload = useFilters()
  const { pageFilters } = filterPayload

  const templatePagesPayload = useTemplatePages({
    filters: {
      ...pageFilters,
      ...filterIds,
    },
    requestOptions: {
      include_template_page_brand: true,
      include_template_page_permission_ids: true,
    },
    performHttpRequests: true,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filteredTemplatePages,
    filteredTemplatePagesCount,
    hasFilteredTemplatePages,
    loading,
  } = templatePagesPayload

  return (
    <DashboardModule
      boxProps={{ id: 'realhub-nav-templates' }}
      contentBoxProps={{ flexDirection: 'column' }}
      headerRight={<DashboardModule.FilterHeader {...filterPayload} />}
      loading={loading}
      title="Templates"
    >
      <DashboardModuleFilters
        filterFields={buildPageFilterFields(allowedFilters)}
        {...filterPayload}
      />

      <Box flexDirection="column">
        {!loading && !hasFilteredTemplatePages && (
        <DashboardModule.BlankState>
          <DashboardModule.BlankState.Title>
            No Templates Present.
          </DashboardModule.BlankState.Title>

          <DashboardModule.BlankState.Paragraph>
            Please add a template or change your filters.
          </DashboardModule.BlankState.Paragraph>
        </DashboardModule.BlankState>
        )}

        {!loading && hasFilteredTemplatePages && (
          // @ts-expect-error TS2322
          <DashboardModule.ScrollableSection maxHeight={600}>
            {filteredTemplatePages.map((templatePage) => {
              const { id } = templatePage

              return (
                <TemplatePageListItem
                  templatePage={templatePage}
                  key={id}
                  showBrandTitle={!filterIds?.agency_id}
                />
              )
            })}
          </DashboardModule.ScrollableSection>
        )}

        <DashboardModule.LoadMoreFooter
          boxProps={{ borderTop: loading ? 'none' : '1px solid' }}
          callbacks={{ loadMore }}
          canLoadMore={canLoadMore}
          entityCount={filteredTemplatePagesCount}
          loading={loading}
        />
      </Box>
    </DashboardModule>
  )
}

export default TemplatePageList
